import {
  ExclamationTriangleIcon,
  InfoCircledIcon,
} from '@radix-ui/react-icons';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useReactivateSubscription } from '../../../../app/hooks/billing/useReactivateSubscription';
import { useSubscription } from '../../../../app/hooks/billing/useSubscription';
import { useDateFnsLocale } from '../../../../app/hooks/useDateFnsLocale';
import { pagePath } from '../../../../app/utils/consts/pagePath';
import cn from '../../../../app/utils/cn';
import { Button } from '../../Button';

type BannerTone = 'warning' | 'danger' | 'critical' | 'info';

type BannerAction = 'navigate-billing' | 'reactivate';

interface IBannerContent {
  tone: BannerTone;
  message: string;
  ctaLabel: string;
  action: BannerAction;
}

const TONE_CLASSES: Record<BannerTone, string> = {
  warning:
    'bg-yellow-50 text-yellow-900 border-yellow-300 dark:bg-yellow-950 dark:text-yellow-100 dark:border-yellow-700',
  danger:
    'bg-red-50 text-red-900 border-red-300 dark:bg-red-950 dark:text-red-100 dark:border-red-700',
  critical:
    'bg-red-100 text-red-950 border-red-500 dark:bg-red-900 dark:text-red-50 dark:border-red-500',
  info: 'bg-blue-50 text-blue-900 border-blue-300 dark:bg-blue-950 dark:text-blue-100 dark:border-blue-700',
};

const ICON_BY_TONE: Record<BannerTone, typeof InfoCircledIcon> = {
  warning: ExclamationTriangleIcon,
  danger: ExclamationTriangleIcon,
  critical: ExclamationTriangleIcon,
  info: InfoCircledIcon,
};

function daysUntil(date: Date): number {
  const diffMs = date.getTime() - Date.now();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}

export default function SubscriptionStatusBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dateLocale = useDateFnsLocale();
  const { subscription, plan, limits, usage, isLoading } = useSubscription();
  const { reactivateAsync, reactivateIsPending } = useReactivateSubscription();

  const content = useMemo<IBannerContent | null>(() => {
    if (!subscription) return null;

    if (subscription.status === 'SUSPENDED') {
      return {
        tone: 'critical',
        message: t('billing.statusBanner.suspended'),
        ctaLabel: t('billing.statusBanner.cta.updatePayment'),
        action: 'navigate-billing',
      };
    }

    if (subscription.status === 'PAST_DUE') {
      return {
        tone: 'danger',
        message: t('billing.statusBanner.pastDue'),
        ctaLabel: t('billing.statusBanner.cta.updatePayment'),
        action: 'navigate-billing',
      };
    }

    if (subscription.status === 'TRIALING' && subscription.trialEndsAt) {
      const daysLeft = daysUntil(subscription.trialEndsAt);
      return {
        tone: 'warning',
        message: t('billing.statusBanner.trialing', {
          count: daysLeft,
          daysLeft,
        }),
        ctaLabel: t('billing.statusBanner.cta.seePlans'),
        action: 'navigate-billing',
      };
    }

    if (subscription.status === 'ACTIVE' && subscription.cancelAt) {
      return {
        tone: 'warning',
        message: t('billing.statusBanner.cancelPending', {
          date: format(subscription.cancelAt, 'dd MMM yyyy', {
            locale: dateLocale,
          }),
        }),
        ctaLabel: t('billing.statusBanner.cancelPendingCta'),
        action: 'reactivate',
      };
    }

    if (
      subscription.status === 'ACTIVE' &&
      plan?.tier === 'SOLO' &&
      limits?.maxActiveLeads !== null &&
      limits?.maxActiveLeads !== undefined &&
      usage &&
      usage.activeLeads / limits.maxActiveLeads >= 0.8
    ) {
      return {
        tone: 'info',
        message: t('billing.statusBanner.nearLimit', {
          current: usage.activeLeads,
          limit: limits.maxActiveLeads,
        }),
        ctaLabel: t('billing.statusBanner.cta.seePlans'),
        action: 'navigate-billing',
      };
    }

    return null;
  }, [subscription, plan, limits, usage, t, dateLocale]);

  if (isLoading || !subscription || !content) return null;

  const Icon = ICON_BY_TONE[content.tone];

  async function handleCta() {
    if (!content) return;
    if (content.action === 'reactivate') {
      try {
        await reactivateAsync();
      } catch {}
      return;
    }
    navigate(pagePath.billing);
  }

  return (
    <div
      role="status"
      className={cn(
        'flex w-full flex-col gap-2 border-b px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between sm:px-4',
        TONE_CLASSES[content.tone],
      )}
    >
      <div className="flex items-start gap-2 sm:items-center">
        <Icon className="mt-0.5 size-4 shrink-0 sm:mt-0" />
        <span className="leading-snug">{content.message}</span>
      </div>
      <Button
        color="ghost"
        onClick={handleCta}
        isLoading={content.action === 'reactivate' && reactivateIsPending}
        className="w-auto self-end border-current bg-transparent px-3 text-xs font-medium text-current hover:bg-current/10 sm:self-auto dark:border-current dark:bg-transparent dark:text-current dark:hover:bg-current/10"
      >
        {content.ctaLabel}
      </Button>
    </div>
  );
}
